import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, useUser } from '@clerk/clerk-react';

import QmsJsonImport from '../components/QmsJsonImport';
import { procedureAuthorPayload } from '../components/qmsAuthoring';

const API_BASE = process.env.REACT_APP_API_URL;
const ACCENT = '#2dd4bf';
const STATUS_COLORS = { Draft:'#94a3b8','In Review':'#60a5fa',Released:'#46BB89',Superseded:'#f59e0b',Obsolete:'#EC2124' };

function StatusBadge({ status }) {
  const c = STATUS_COLORS[status]||'#666';
  return <span style={{ display:'inline-block',padding:'2px 10px',borderRadius:20,fontSize:11,
    fontWeight:700,letterSpacing:'0.05em',textTransform:'uppercase',background:c+'22',color:c }}>{status}</span>;
}
function normalizeRole(r) {
  if (!r) return ''; if (Array.isArray(r)) return (r[0]||'').toLowerCase().trim();
  return String(r).toLowerCase().trim();
}

export default function AssemblyInstructionList() {
  const { getToken } = useAuth(); const { user } = useUser(); const navigate = useNavigate();
  const role = normalizeRole(user?.publicMetadata?.role) || normalizeRole(user?.publicMetadata?.roles);
  const canEdit = ['admin','quality-manager','production-engineer','service'].includes(role) || ['gpv','ubiqconn','acmepoint','hk_ringer'].includes(String(role||'').replace(/^test-/,''));

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status:'Released', productFamily:'', search:'' });
  const [cloning, setCloning] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const token = await getToken();
      const params = new URLSearchParams();
      if (filters.status)        params.set('status',filters.status);
      if (filters.productFamily) params.set('productFamily',filters.productFamily);
      if (filters.search)        params.set('search',filters.search);
      const res = await window.fetch(`${API_BASE}/quality/assembly-instructions?${params}`, {
        headers:{ Authorization:`Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error(data.message || data.error || 'Could not load documents');
      if (data.ok) setItems(data.items||[]);
    } catch (err) { setError(err.message); } finally { setLoading(false); }
  }, [getToken,filters]);

  useEffect(()=>{ load(); },[load]);
  const setFilter=(k,v)=>setFilters(f=>({...f,[k]:v}));

  const clone = async (e, item) => {
    e.stopPropagation();
    setCloning(item._id);
    try {
      const token = await getToken();
      const [detailRes, docNumRes] = await Promise.all([
        window.fetch(`${API_BASE}/quality/assembly-instructions/${item._id}`, { headers:{ Authorization:`Bearer ${token}` } }),
        window.fetch(`${API_BASE}/quality/next-doc-number?prefix=AI-NIN-`, { headers:{ Authorization:`Bearer ${token}` } }),
      ]);
      const detail = await detailRes.json();
      const docNum = await docNumRes.json();
      if (!detailRes.ok || !detail.ok || !docNumRes.ok || !docNum.ok) throw new Error(detail.message || detail.error || 'Could not prepare draft copy');
      const rest = procedureAuthorPayload(detail.instruction);
      const createRes = await window.fetch(`${API_BASE}/quality/assembly-instructions`, {
        method:'POST', headers:{ Authorization:`Bearer ${token}`, 'Content-Type':'application/json' },
        body: JSON.stringify({ ...rest, documentNumber:docNum.documentNumber, name:`${rest.name} (Copy)` }),
      });
      const created = await createRes.json();
      if (!createRes.ok || !created.ok) throw new Error(created.message || created.error || 'Could not create draft copy');
      if (created.ok) navigate(`/quality/assembly-instructions/${created.instruction._id}`);
    } catch (err) { setError(err.message); } finally { setCloning(null); }
  };
  const fs={ background:'var(--ni-panel)',border:'1px solid var(--ni-border-soft)',
    color:'var(--ni-text)',borderRadius:6,padding:'6px 10px',fontSize:13 };

  return (
    <div>
      <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:20 }}>
        <h2 style={{ margin:0,fontSize:20,fontWeight:800 }}>Assembly Instructions</h2>
        {canEdit && (
          <button onClick={()=>navigate('/quality/assembly-instructions/new')}
            style={{ background:ACCENT,color:'#000',border:'none',borderRadius:6,
              padding:'8px 18px',fontWeight:700,fontSize:13,cursor:'pointer' }}>+ New Instruction</button>
        )}
      </div>

      {canEdit && <QmsJsonImport kind="assembly" onCreated={data => navigate(`/quality/assembly-instructions/${data.instruction._id}`)} />}
      {error && <p role="alert" style={{ color:'#EC2124' }}>{error}</p>}

      <div style={{ display:'flex',gap:10,marginBottom:16,flexWrap:'wrap' }}>
        <input placeholder="Search…" value={filters.search}
          onChange={e=>setFilter('search',e.target.value)}
          style={{ ...fs,minWidth:200 }} />
        {canEdit && (
          <select value={filters.status} onChange={e=>setFilter('status',e.target.value)} style={fs}>
            <option value="">All statuses</option>
            {['Draft','In Review','Released','Superseded','Obsolete'].map(s=><option key={s}>{s}</option>)}
          </select>
        )}
        <input placeholder="Product family…" value={filters.productFamily}
          onChange={e=>setFilter('productFamily',e.target.value)} style={fs} />
      </div>

      <div style={{ background:'var(--ni-panel)',border:'1px solid var(--ni-border-soft)',borderRadius:10,overflow:'hidden' }}>
        {loading ? <div style={{ padding:40,textAlign:'center',color:'var(--ni-text-muted)' }}>Loading…</div>
        : items.length===0 ? (
          <div style={{ padding:40,textAlign:'center',color:'var(--ni-text-muted)' }}>
            No assembly instructions found.{' '}
            {canEdit && <button onClick={()=>navigate('/quality/assembly-instructions/new')}
              style={{ background:'none',border:'none',color:ACCENT,cursor:'pointer',fontSize:14 }}>Create one.</button>}
          </div>
        ) : (
          <table style={{ width:'100%',borderCollapse:'collapse',fontSize:13 }}>
            <thead>
              <tr style={{ color:'var(--ni-text-muted)',fontSize:11,textTransform:'uppercase',
                letterSpacing:'0.06em',background:'rgba(0,0,0,0.2)' }}>
                {['Doc No.','Name','Product Family','Est.','Rev.','Status','Updated'].map(h=>(
                  <th key={h} style={{ padding:'10px 16px',textAlign:'left',fontWeight:600,whiteSpace:'nowrap' }}>{h}</th>
                ))}
                {canEdit && <th style={{ padding:'10px 16px',textAlign:'left',fontWeight:600 }}></th>}
              </tr>
            </thead>
            <tbody>
              {items.map(item=>(
                <tr key={item._id} onClick={()=>navigate(`/quality/assembly-instructions/${item._id}`)}
                  style={{ cursor:'pointer',borderTop:'1px solid var(--ni-border-soft)' }}
                  onMouseEnter={ev=>ev.currentTarget.style.background='rgba(255,255,255,0.03)'}
                  onMouseLeave={ev=>ev.currentTarget.style.background='transparent'}>
                  <td style={{ padding:'11px 16px',fontFamily:'monospace',fontSize:12,color:ACCENT,whiteSpace:'nowrap' }}>{item.documentNumber}</td>
                  <td style={{ padding:'11px 16px',fontWeight:600 }}>{item.name}</td>
                  <td style={{ padding:'11px 16px',color:'var(--ni-text-muted)' }}>{item.productFamily||'—'}</td>
                  <td style={{ padding:'11px 16px',color:'var(--ni-text-muted)',whiteSpace:'nowrap' }}>
                    {item.estimatedMinutes?`${item.estimatedMinutes} min`:'—'}
                  </td>
                  <td style={{ padding:'11px 16px',color:'var(--ni-text-muted)',fontFamily:'monospace' }}>{item.currentRevision}</td>
                  <td style={{ padding:'11px 16px',whiteSpace:'nowrap' }}><StatusBadge status={item.currentStatus}/></td>
                  <td style={{ padding:'11px 16px',color:'var(--ni-text-muted)',whiteSpace:'nowrap' }}>
                    {item.updatedAt?new Date(item.updatedAt).toLocaleDateString():'—'}
                  </td>
                  {canEdit && (
                    <td style={{ padding:'6px 16px' }} onClick={ev=>ev.stopPropagation()}>
                      <button onClick={ev=>clone(ev, item)} disabled={cloning===item._id}
                        style={{ background:'none', border:`1px solid ${ACCENT}44`, color:ACCENT, borderRadius:4,
                          padding:'3px 10px', fontSize:11, cursor:'pointer', whiteSpace:'nowrap', opacity:cloning===item._id?0.5:1 }}>
                        {cloning===item._id ? '…' : 'Draft copy'}
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <div style={{ marginTop:10,color:'var(--ni-text-muted)',fontSize:12 }}>{items.length} instruction{items.length!==1?'s':''}</div>
    </div>
  );
}
